import React, { useMemo } from 'react';
import {
  Box, Button, Chip, Container, Divider, Grid, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import WorkIcon from '@mui/icons-material/Work';
import BadgeIcon from '@mui/icons-material/Badge';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import Seo from '../../components/Seo';
import aboutConfig from '../../assets/configs/aboutConfig';
import footerConfig from '../../assets/configs/footerConfig';
import { useLang } from '../../utils/i18n';
import { withUtm } from '../../utils/withUtm';
import { openScheduleCall } from '../../utils/scheduleCall';

const EMAIL = process.env.EMAIL || 'martin.antunez.ai@gmail.com';
const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const UI = {
  en: {
    seoTitle: 'About | Martín Antúnez',
    seoDesc: 'Healthcare AI & Data Scientist, PhD in Neuroscience — from clinical research to production AI. US Permanent Resident (NIW).',
    subtitle: 'Healthcare AI & Data Scientist · PhD in Neuroscience',
    niw: 'US Permanent Resident — NIW',
    niwTooltip: 'The National Interest Waiver (NIW) is a merit-based US green card granted to individuals whose work is deemed to be in the national interest — no employer sponsorship required. It recognises exceptional expertise and research contributions.',
    education: 'Education',
    openTo: 'Open to',
    map: 'Science and Technology Know No Borders',
    mapSub: 'Countries I have worked for or collaborated with',
    contact: "Let's Connect",
    contactSub: 'Open to consulting engagements and full-time roles in the United States.',
    cta: "Contact",
    book: "Book a call",
  },
  es: {
    seoTitle: 'Sobre mí | Martín Antúnez',
    seoDesc: 'Científico de Datos de Salud e IA, PhD en Neurociencia — de la investigación clínica a la IA en producción. Residente permanente en EEUU (NIW).',
    subtitle: 'Científico de Datos de Salud e IA · PhD en Neurociencia',
    niw: 'Residente permanente en EEUU — NIW',
    niwTooltip: 'La National Interest Waiver (NIW) es una green card estadounidense basada en méritos, concedida a personas cuyo trabajo se considera de interés nacional — sin necesidad de patrocinio de un empleador. Reconoce una experiencia y contribuciones de investigación excepcionales.',
    education: 'Educación',
    openTo: 'Disponible para',
    map: 'La ciencia y la tecnología no entienden de fronteras',
    mapSub: 'Países en los que he trabajado o con los que he colaborado',
    contact: 'Conectemos',
    contactSub: 'Disponible para consultoría y posiciones de tiempo completo en Estados Unidos.',
    cta: 'Contactar',
    book: 'Agendar una llamada',
  },
};

const availIcons = { briefcase: <WorkIcon />, badge: <BadgeIcon /> };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (delay = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function About() {
  const [lang] = useLang();
  const t = UI[lang] || UI.en;
  const bio = aboutConfig.bio_i18n[lang] || aboutConfig.bio_i18n.en;
  const availability = aboutConfig.availability_i18n[lang] || aboutConfig.availability_i18n.en;
  const educationGroups = aboutConfig.educationGroups || [];

  const highlightedIsos = useMemo(
    () => new Set(aboutConfig.collaboratorCountries.map((c) => c.iso)),
    [],
  );

  const mailHref = withUtm(
    `mailto:${EMAIL}?subject=${encodeURIComponent('Consulting Inquiry')}`,
    'about_cta',
  );

  return (
    <>
      <Seo title={t.seoTitle} description={t.seoDesc} />

      {/* ── HERO ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #0a1628 0%, #060c14 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          pt: { xs: 7, md: 11 },
          pb: { xs: 7, md: 10 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">

            {/* Left: text */}
            <Grid item xs={12} md={7}>
              <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 900, letterSpacing: -1, mb: 1, color: '#e2e8f0' }}
                >
                  Martín Antúnez
                </Typography>
                <Typography
                  sx={{ color: '#7dd3fc', fontWeight: 600, fontSize: 17, mb: 3 }}
                >
                  {t.subtitle}
                </Typography>

                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
                  <Tooltip
                    title={t.niwTooltip}
                    arrow
                    placement="bottom"
                  >
                    <Chip
                      icon={<VerifiedIcon />}
                      label={t.niw}
                      sx={{
                        bgcolor: 'rgba(34,197,94,0.1)',
                        border: '1px solid rgba(34,197,94,0.3)',
                        color: '#22c55e',
                        fontWeight: 700,
                        cursor: 'help',
                        '& .MuiChip-icon': { color: '#22c55e' },
                      }}
                    />
                  </Tooltip>
                  <Chip
                    icon={<LocationOnIcon />}
                    label={lang === 'es' ? 'Estados Unidos' : 'United States'}
                    sx={{
                      bgcolor: 'rgba(6,182,212,0.08)',
                      border: '1px solid rgba(6,182,212,0.2)',
                      color: '#7dd3fc',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#7dd3fc' },
                    }}
                  />
                </Stack>

                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {availability.map((a) => (
                    <Chip
                      key={a.id}
                      icon={availIcons[a.icon]}
                      label={a.title}
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(255,255,255,0.15)',
                        color: 'text.secondary',
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: '#7dd3fc' },
                      }}
                    />
                  ))}
                </Stack>
              </motion.div>
            </Grid>

            {/* Right: photo */}
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'center' } }}>
              <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0.15}>
                <Box
                  component="img"
                  src={aboutConfig.photo}
                  alt="Martín Antúnez"
                  sx={{
                    width: { xs: 220, md: 340 },
                    height: { xs: 220, md: 340 },
                    objectFit: 'cover',
                    objectPosition: 'center 35%',
                    borderRadius: '50%',
                    border: '3px solid rgba(6,182,212,0.35)',
                    boxShadow: '0 0 48px rgba(6,182,212,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── BIO ── */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 9 } }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          variants={fadeUp}
          custom={0}
        >
          <Stack spacing={3}>
            {bio.map((para, i) => (
              <Typography
                key={i}
                sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.85, color: 'rgba(226,232,240,0.82)' }}
              >
                {para}
              </Typography>
            ))}
          </Stack>
        </motion.div>
      </Container>

      {/* ── EDUCATION ── */}
      {educationGroups.length > 0 && (
        <Container maxWidth="md" sx={{ pb: { xs: 6, md: 9 } }}>
          <Divider sx={{ mb: { xs: 5, md: 6 }, borderColor: 'rgba(255,255,255,0.07)' }} />
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            custom={0}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, letterSpacing: -0.3, mb: 3.5, color: 'rgba(226,232,240,0.75)' }}
            >
              {t.education}
            </Typography>
            <Stack spacing={3.5}>
              {educationGroups.map((group) => {
                const groupTitle = group.title_i18n?.[lang] || group.title_i18n?.en;
                return (
                  <Box key={group.id}>
                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: '#7dd3fc', opacity: 0.75, mb: 1.5 }}
                    >
                      {groupTitle}
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1.25}>
                      {group.items.map((item) => {
                        const label = item.label_i18n?.[lang] || item.label_i18n?.en;
                        const tooltip = item.tooltip_i18n?.[lang] || item.tooltip_i18n?.en;
                        return (
                          <Tooltip key={item.id} title={tooltip} arrow placement="top">
                            <Chip
                              icon={<SchoolIcon />}
                              label={label}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.06)',
                                color: 'rgba(226,232,240,0.9)',
                                border: '1px solid rgba(255,255,255,0.12)',
                                fontWeight: 600,
                                cursor: 'help',
                                '& .MuiChip-icon': { color: '#7dd3fc' },
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </motion.div>
        </Container>
      )}

      {/* ── OPEN TO ── */}
      <Box sx={{ bgcolor: 'background.paper', borderTop: '1px solid rgba(255,255,255,0.07)', borderBottom: '1px solid rgba(255,255,255,0.07)', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            custom={0}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 900, letterSpacing: -0.5, mb: 4, textAlign: 'center' }}
            >
              {t.openTo}
            </Typography>
            <Grid container spacing={3}>
              {availability.map((a) => (
                <Grid item xs={12} sm={6} key={a.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: '1px solid rgba(255,255,255,0.09)',
                      borderRadius: 3,
                      textAlign: 'center',
                      transition: 'border-color .2s, transform .2s',
                      '&:hover': { borderColor: 'rgba(6,182,212,0.35)', transform: 'translateY(-3px)' },
                    }}
                  >
                    <Box sx={{ color: '#7dd3fc', mb: 1.5, '& svg': { fontSize: 36 } }}>
                      {availIcons[a.icon]}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                      {a.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {a.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* ── WORLD MAP ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 9 } }}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
          custom={0}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, letterSpacing: -0.5, mb: 1, textAlign: 'center' }}
          >
            {t.map}
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 4, fontSize: 14 }}
          >
            {t.mapSub}
          </Typography>

          <Box
            sx={{
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: '#060c14',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            }}
          >
            <ComposableMap
              projectionConfig={{ scale: 145, center: [10, 10] }}
              style={{ width: '100%', height: 'auto' }}
            >
              <Geographies geography={GEO_URL}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const isHighlighted = highlightedIsos.has(String(geo.id));
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={isHighlighted ? 'rgba(6,182,212,0.55)' : '#0f1e35'}
                        stroke="#060c14"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none' },
                          hover: {
                            fill: isHighlighted ? '#06B6D4' : '#152240',
                            outline: 'none',
                          },
                          pressed: { outline: 'none' },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>
          </Box>

          {/* Country chips legend */}
          <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center" sx={{ mt: 3 }}>
            {aboutConfig.collaboratorCountries.map((c) => (
              <Chip
                key={c.id}
                label={lang === 'es' ? c.nameEs : c.name}
                size="small"
                sx={{
                  bgcolor: 'rgba(6,182,212,0.08)',
                  border: '1px solid rgba(6,182,212,0.22)',
                  color: '#7dd3fc',
                  fontWeight: 600,
                }}
              />
            ))}
          </Stack>
        </motion.div>
      </Container>

      {/* ── CONTACT ── */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #060c14 0%, #0a1628 100%)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-60px' }}
            variants={fadeUp}
            custom={0}
          >
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: -0.5, mb: 1.5 }}>
              {t.contact}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 4, fontSize: 15 }}>
              {t.contactSub}
            </Typography>

            <Stack
              direction="column"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Button
                onClick={openScheduleCall}
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CalendarMonthIcon />}
                sx={{ px: 5, py: 1.5, fontSize: 16 }}
              >
                {t.book}
              </Button>
              <Button
                href={mailHref}
                variant="outlined"
                size="large"
                startIcon={<EmailIcon />}
                sx={{
                  px: 5,
                  py: 1.5,
                  fontSize: 16,
                  color: 'rgba(226,232,240,0.85)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { borderColor: '#06B6D4', color: '#06B6D4' },
                }}
              >
                {t.cta}
              </Button>
            </Stack>

            <Typography
              component="a"
              href={mailHref}
              sx={{
                display: 'block',
                mb: 5,
                fontSize: 14,
                color: 'rgba(226,232,240,0.45)',
                textDecoration: 'none',
                transition: 'color .2s',
                '&:hover': { color: '#06B6D4' },
              }}
            >
              {EMAIL}
            </Typography>

            <Stack direction="row" spacing={3} justifyContent="center">
              {footerConfig.icons.map((item) => (
                <Box
                  key={item.id}
                  component="a"
                  href={item.url}
                  target={item.target}
                  rel="noopener noreferrer"
                  sx={{
                    color: 'rgba(226,232,240,0.45)',
                    transition: 'color .2s',
                    '&:hover': { color: '#06B6D4' },
                    '& svg': { display: 'block' },
                  }}
                >
                  {React.cloneElement(item.icon, { size: 26 })}
                </Box>
              ))}
            </Stack>
          </motion.div>
        </Container>
      </Box>
    </>
  );
}
